import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { token } from "@atlaskit/tokens";

import { Button } from "@/libraries/design/components/button/src/button";
import { wacToken } from "@/libraries/design/foundations";

import { Picture } from "../../components/wpl/image/src/image";

const stickyWrapperStyle = css({
    lineHeight: 1,
    boxSizing: "border-box",
    margin: 0,
    padding: 0,
    border: 0,
    fontSize: "100%",
    verticalAlign: "baseline",
    order: -1
});

const imktNavbarStyle = css({
    lineHeight: 1,
    boxSizing: "inherit",
    margin: 0,
    padding: 0,
    border: 0,
    fontSize: "100%",
    verticalAlign: "baseline",
    display: "block",
    order: -1,
    width: "100%"
});

const containerFluidStyle = css({
    lineHeight: 1,
    boxSizing: "border-box",
    margin: 0,
    padding: 0,
    border: 0,
    fontSize: "100%",
    verticalAlign: "baseline",
    backgroundColor: wacToken("color.neutral.light0"),
    boxShadow: token("elevation.shadow.overlay"),
    minWidth: "100%",
    clear: "both",
    position: "relative",
    maxHeight: "140px",
    overflow: "hidden",
    zIndex: 9,
    minHeight: "72px"
});

const rowStyle = css({
    "lineHeight": 1,
    "boxSizing": "border-box",
    "margin": 0,
    "padding": 0,
    "border": 0,
    "fontSize": "100%",
    "verticalAlign": "baseline",

    "marginInline": "auto",
    "maxWidth": 1200,
    "paddingInline": 32,

    // TABLET: bring content slightly more inwards (<= 1024px)
    "@media (max-width: 1024px)": {
        paddingInline: 40
    },

    // SMALL / PHONE: slightly tighter (<= 680px)
    "@media (max-width: 680px)": {
        paddingInline: 32
    }
});

const columnStyle = css({
    lineHeight: 1,
    boxSizing: "border-box",
    margin: 0,
    padding: 0,
    border: 0,
    fontSize: "100%",
    verticalAlign: "baseline",

    width: "100%",
    minHeight: 1,
    display: "flex",
    flexFlow: "row nowrap",
    alignItems: "center",
    justifyContent: "space-between",

    // Ensure minimum 32px between logo block and button block
    columnGap: 32
});

const titleWrapperStyle = css({
    lineHeight: 1,
    boxSizing: "border-box",
    margin: 0,
    padding: 0,
    border: 0,
    fontSize: "100%",
    verticalAlign: "baseline",
    display: "flex",
    flexDirection: "row",
    alignItems: "center"
});

const linkStyle = css({
    "lineHeight": 1,
    "boxSizing": "border-box",
    "margin": 0,
    "border": 0,
    "fontSize": "100%",
    "verticalAlign": "baseline",
    "color": wacToken("color.primary.blue"),
    "display": "block",
    "transformStyle": "preserve-3d",
    "height": 60,
    "textDecoration": "none",

    // MOBILE / TABLET: small inset
    "paddingInlineStart": 8,
    "paddingInlineEnd": 8,

    // DESKTOP: push logo inward to visually match button’s inset (> 1024px)
    "@media (min-width: 1025px)": {
        paddingInlineStart: 80,
        paddingInlineEnd: 8
    }
});

const rightNavStyle = css({
    "lineHeight": 1,
    "boxSizing": "border-box",
    "margin": 0,
    "padding": 0,
    "border": 0,
    "fontSize": "100%",
    "verticalAlign": "baseline",
    "paddingBlockStart": 15,
    "paddingBlockEnd": 15,

    // DESKTOP: mirror the logo side’s padding so the button ends the same distance from the right edge (> 1024px)
    "@media (min-width: 1025px)": {
        paddingInlineEnd: 24,
        paddingInlineStart: 24
    },

    // On small viewports, remove the button completely so it never wraps (<= 680px)
    "@media (max-width: 680px)": {
        display: "none"
    }
});

const logoStyle = css({
    "lineHeight": 1,
    "boxSizing": "border-box",
    "margin": 0,
    "padding": 0,
    "border": 0,
    "fontSize": "100%",
    "color": wacToken("color.primary.blue"),
    "height": 30,
    "maxWidth": "100%",
    "marginBlockEnd": 16,
    "verticalAlign": "bottom",
    "position": "relative",
    "insetBlockStart": "50%",
    "transform": "translateY(-50%)",
    "pointerEvents": "none",

    // Slight shrink on tablet (<= 1024px)
    "@media (max-width: 1024px)": {
        height: 26
    },

    // Smaller on small screens (<= 680px)
    "@media (max-width: 680px)": {
        height: 22
    }
});

export const SourcetreeAppNav = () => {
    const intl = useIntl();
    const buttonText = intl.formatMessage({
        id: "sourcetreeapp.globalnav.button.download.free",
        defaultMessage: "Download free"
    });

    return (
        <div css={stickyWrapperStyle}>
            <nav css={imktNavbarStyle}>
                <div css={containerFluidStyle}>
                    <div css={rowStyle}>
                        <div css={columnStyle}>
                            <div css={titleWrapperStyle}>
                                <a href="/" css={linkStyle}>
                                    <Picture
                                        image={{
                                            src: "https://wac-cdn.atlassian.com/dam/jcr:f32681c1-355d-4806-b29c-319b0c6ecb06/Sourcetree-blue.svg?cdnVersion=3108",
                                            alt: "Sourcetree logo"
                                        }}
                                        css={logoStyle}
                                    />
                                </a>
                            </div>
                            <div css={rightNavStyle}>
                                <Button
                                    variant="primary"
                                    size="small"
                                    color="blue"
                                    href="https://product-downloads.atlassian.com/software/sourcetree/ga/Sourcetree_4.2.14_297.zip"
                                    dataEventProps={{
                                        "data-event": "clicked",
                                        "data-event-component": "linkButton",
                                        "data-event-container": "navbar",
                                        "data-product-key": "sourcetreeapp",
                                        "data-label-english": "Download free"
                                    }}
                                >
                                    {buttonText}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </nav>
        </div>
    );
};
